import { useState, useMemo } from 'react';
import PageHeader from '../../Common/PageHeader';
import Banner from '../../Common/Banners';
import Icons from '../../../../components/Common/Icons/Icons';
import Notices from './Notices';
import Documents from './Documents';
import Accounts from './Accounts';
import { accountData, IAccountData } from '../../Layout/Account/Data';
import Footer from '../../Layout/Footer';
import { cn } from '../../../../utils/styles';
import NoticesDialog from '../../../pages/financial-institution/inner-application/NoticesDIalog';
import UnreviewedDocDialog from './UnreviewedDocDialog';
import { Input } from '../../../../components/Common/UI/Input';

interface DashboardProps {
  previewMode?: boolean;
}

const SEARCH_SUGGESTIONS = ['Payroll'];
const viewerUpdatesRaw = localStorage.getItem('viewerUpdates');

const ALLOWED_ACCOUNTS_FOR_NON_ADMIN = viewerUpdatesRaw
  ? (JSON.parse(viewerUpdatesRaw).find((u: IAccountData) => u.id === '1')?.selectedAccounts ?? [])
  : ['****4560', '****4564'];

function Dashboard({ previewMode }: DashboardProps) {
  const [searchVal, setSearchVal] = useState('');
  const [open, setOpen] = useState(false);
  const tempViewers = JSON.parse(localStorage.getItem('tempViewers') || '[]');
  const isAuthorized = tempViewers.length > 0;

  const filteredAccountData = useMemo(() => {
    if (!isAuthorized) return accountData;

    return accountData
      .map(accountType => ({
        ...accountType,
        accounts: accountType.accounts.filter(account => ALLOWED_ACCOUNTS_FOR_NON_ADMIN.includes(account.number)),
      }))
      .filter(accountType => accountType.accounts.length > 0);
  }, [isAuthorized]);

  const checkingData = filteredAccountData.filter(el => el.type === 'checking');
  const savingsData = filteredAccountData.filter(el => el.type === 'savings');
  const loanData = filteredAccountData.filter(el => el.type === 'loan');
  const businessData = filteredAccountData.filter(el => el.type === 'business');

  const filtered = SEARCH_SUGGESTIONS.filter(item => item.toLowerCase().includes(searchVal.toLowerCase()));

  return (
    <div>
      <NoticesDialog />
      <UnreviewedDocDialog />

      <PageHeader
        icon={<Icons.Home />}
        title="Dashboard"
        actions={
          <div className="relative max-w-60">
            <Input
              placeholder="Search"
              variant="home"
              inputClassName="border-[#FFFFFF]"
              value={searchVal}
              icon={<Icons.Search />}
              onBlur={() => setTimeout(() => setOpen(false), 150)}
              onChange={e => {
                setSearchVal(e.target.value);
                setOpen(!!e.target.value);
              }}
            />

            {open && searchVal.trim() && filtered.length > 0 && (
              <div className="absolute z-50 mt-1 w-full rounded-md bg-white shadow">
                {filtered.map(item => (
                  <div
                    key={item}
                    tabIndex={0}
                    role="button"
                    className="px-3 py-2 text-sm cursor-pointer hover:bg-gray-100"
                    onMouseDown={() => {
                      setSearchVal(item);
                      setOpen(false);
                    }}
                  >
                    {item}
                  </div>
                ))}
              </div>
            )}
          </div>
        }
      />
      <Banner position="headerSpace" />
      <div className={cn('px-[20px] grid gap-[20px]', { 'min-w-[900px]': previewMode })}>
        <Notices search={searchVal} />
        <Documents search={searchVal} />
        <div className="grid lg:grid-cols-2 gap-[20px]">
          {checkingData.length > 0 && (
            <Accounts
              title="Checking"
              accountStyle="border-l-addition6"
              icon={<Icons.UserCheck />}
              iconStyle="bg-addition6 border-addition6"
              data={checkingData}
            />
          )}
          {loanData.length > 0 && (
            <Accounts
              title="Loan"
              icon={<Icons.Loan />}
              accountStyle="border-l-addition4"
              iconStyle="bg-addition4 border-addition4"
              data={loanData}
            />
          )}
          {!isAuthorized && savingsData.length > 0 && (
            <Accounts
              title="Savings"
              icon={<Icons.Saving />}
              accountStyle="border-l-addition5"
              iconStyle="bg-addition5 border-addition5"
              data={savingsData}
            />
          )}
          {!isAuthorized && businessData.length > 0 && (
            <Accounts
              title="Premier Business Checking"
              icon={<Icons.UserCheck />}
              accountStyle="border-l-addition1"
              iconStyle="bg-addition1 border-addition1"
              data={businessData}
            />
          )}
        </div>

        <Footer />
      </div>
    </div>
  );
}

Dashboard.defaultProps = {
  previewMode: false,
};

export default Dashboard;
